/**
 * =============================================================================
 * IMPORTANT, READ THIS MESSAGE BEFORE EDITING THIS FILE.
 *
 * This file needs to be kept tightly in sync with its corresponding version in
 * the backend. If you are changing this file, please also make the
 * corresponding changes in `cip-backend:functions/src/sections/sectionDocument.ts`
 * and submit both PRs simultaneously.
 *
 * Be careful because import paths may be different. Don't forget to double
 * check that you update the comments in `cip-backend` as well.
 *
 * Previously lived at
 * `src/apps/webapp/src/course/section/models/sectionDocument.ts`; moved into
 * `@cip/core` so downstream helpers (e.g. `computeSectionDensityFromSections`)
 * can reference a single canonical shape. The old webapp path still re-exports
 * from here for churn-avoidance. See the tracking issue linked from that file
 * for removing the re-export and migrating the converter to
 * `createConverter` + a Zod schema.
 * =============================================================================
 */
import {z} from 'zod';

import type {CourseId, UserId} from '../../../types/branded';
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- CourseDocument is referenced in the TSDoc link for `timeIndex`.
import type {CourseDocument} from '../course';
import type {SectionId, SectionWeekId} from '../../../section/sectionApi';
import {FirestoreTimestampSchema, type FirebaseTimestamp} from '../../types';

/** ============== COPY EVERYTHING STARTING HERE TO cip-backend ============= */

/**
 * Minimum shape stored for a section cover in `SectionDocument.covers`.
 *
 * `courseRole` is the numeric value of the webapp's `Role` enum. It is typed
 * as `number` here rather than the enum itself so this package doesn't have
 * to take a hard dependency on the enum's declaration, and so webapp
 * `ProfileInfo` values (which use the enum) remain structurally assignable.
 */
export interface SectionCoverProfile {
  /** The display name of the user who covered. */
  name: string;
  /** The user id of the user who covered. */
  userId: UserId;
  /** The numeric `Role` value of the user who covered. */
  courseRole: number;
}

/**
 * What Zoom reported about one week's section meeting, stored in
 * `SectionDocument.zoomPresence`.
 */
export interface SectionZoomPresence {
  /**
   * When the meeting started. Section meetings allow join-before-host, so a
   * student may have started it; this does not show the teacher arrived.
   */
  startedAt?: FirebaseTimestamp;
  /** When the meeting's host account most recently joined. */
  hostJoinedAt?: FirebaseTimestamp;
  /**
   * When one of the section's teachers joined as a guest rather than as the
   * host account (`isSectionTeacherGuest` in `@cip/core/zoom`).
   */
  teacherJoinedAt?: FirebaseTimestamp;
}

/**
 * Firestore shape for a `sections/{sectionId}` document.
 *
 * Every field is optional because the Firestore collection is sparse — new
 * docs are written with partial data and later filled in by various triggers.
 * Consumers should guard each access.
 */
export interface SectionDocument {
  /**
   * The (human-readable) name of the section.
   */
  name?: string;
  /**
   * The course id of the section.
   */
  courseId?: CourseId;
  /**
   * The user id of the section leader.
   */
  sectionLeader?: UserId;
  /**
   * The URL of the banner image for the section.
   */
  bannerImgUrl?: string;
  /**
   * The user id of the user who created the zoom meeting. Should be the section
   * leader.
   */
  zoomCreatedBy?: UserId;
  /**
   * The link to join the zoom meeting as a guest.
   */
  zoomJoinLink?: string;
  /**
   * The link to start the zoom meeting as a host. This is the link that the
   * section leader will use to start the meeting. Authenticates the section
   * leader as the CIP owned Zoom account via a ZAK token. See
   * https://developers.zoom.us/docs/meeting-sdk/auth/#start-meetings-and-webinars-with-a-zoom-users-zak-token
   */
  zoomStartLink?: string;
  /**
   * The email address of the CIP owned Zoom account.
   */
  zoomUserEmail?: string;
  /**
   * The Zoom user id of the CIP owned Zoom account.
   */
  zoomUserId?: string;
  /**
   * The id of the zoom meeting.
   */
  zoomMeetingId?: string;
  /**
   * External Zoom link used by Foothill College for managing sections.
   */
  zoomLink?: string;
  /**
   * The date and time the zoom meeting was created. Modelled with the
   * {@link FirebaseTimestamp} structural interface so both the client and
   * admin SDK `Timestamp` classes assign to it without this package having to
   * import either SDK.
   */
  zoomMeetingCreatedAt?: FirebaseTimestamp;
  /**
   * The "time index" of the section. This is the number of hours to add to the
   * first section timestamp (see
   * {@link CourseDocument.firstSectionTimestamp}) to get the section's start
   * time. May be negative (to indicate that the section is before the "first"
   * i.e. baseline section). Specifying 'none' signals that the section is not
   * valid/should be ignored.
   */
  timeIndex?: number | 'none';
  /**
   * The week index of the section.
   */
  weekIndex?: SectionWeekId;
  /**
   * The members of the section. Notably does not include the section leader.
   */
  members?: UserId[];
  /**
   * The guests of the section for each week. Updated by `createZoomMeeting`.
   */
  guests?: Record<SectionWeekId, UserId[]>;
  /**
   * Whether an authorized teacher has started the meeting for each week.
   * Set to `true` when a user with a teaching role (mentor or higher,
   * resolved against `users/{uid}/roles/{courseId}`) calls the
   * `createZoomMeeting` callable AND is one of:
   * - the assigned section leader (`sectionLeader === userId`), or
   * - the SL who signed up to cover this week (`covers[week].userId`), or
   * - any Code-in-Place staff (TA or higher), who have organizational
   *   authority to teach / rescue any section.
   * Drive-by joins by other teaching-team users (mentors and SLs who
   * neither lead nor cover this section) MUST NOT flip this —
   * `/sectioncovers` (`needsCoverCheck`) and the join-section reroute
   * logic both treat this as "an authorized teacher has started the
   * session."
   *
   * Indexing: keys are **0-based week indices**. `teacherAttendance[0]`
   * is course week 1, `teacherAttendance[1]` is course week 2, etc. The
   * writer (`getSectionWeekIdx` in `zoom.ts`) returns `0` for the first
   * week of the course; the reader (`hasActiveTeacher`) subtracts `1`
   * from the 1-based `courseSectionWeek` before looking up. Note that
   * despite the shared `SectionWeekId` brand, sibling fields
   * `sectionLeaderRSVP` and `covers` are keyed by 1-based course week
   * — see https://github.com/CodeinPlace/cip/issues/1484. Don't trust
   * the brand; check each field's convention.
   *
   * Written by `createZoomMeeting` in
   * `cip-backend:functions/src/zooms/zoom.ts`.
   */
  teacherAttendance?: Record<SectionWeekId, boolean>;
  /**
   * What Zoom reported about each week's meeting, keyed by **0-based week
   * index** like `teacherAttendance`. Written by the Zoom webhook from
   * `getSectionZoomPresenceUpdate` in `@cip/core/zoom`. `teacherAttendance`
   * records a teacher's Join click; `hostJoinedAt` here records the host
   * actually reaching Zoom. A week with no entry means no webhook arrived.
   */
  zoomPresence?: Record<string, SectionZoomPresence>;
  /**
   * Whether the section is active. Created sections are not active until
   * set by an admin.
   */
  isActive?: boolean;
  /**
   * The SL's RSVP status for each course week, mirrored from
   * `sections/{id}/rsvp/{slUserId}` by the `cacheSectionLeaderRSVP`
   * trigger and policed weekly by `scheduleRsvpDriftScan`.
   *
   * **Keys are 1-based.** `'1'` is the first course week, never `'0'`.
   * Same convention as the source {@link SectionRsvpDocument} and as
   * {@link CourseWeekId}. Example shape (real cip6 cache):
   * `{'1': true, '2': false, isSectionLeader: true}`. Consumers using
   * a 0-based week index (the reminder cron's `getSectionWeekNumber`,
   * `computeNextSectionSlot`'s `sectionWeekIdx`) **must add 1** before
   * keying — `String(0)` always misses the first course week.
   *
   * The TypeScript type uses `SectionWeekId` for historical reasons,
   * but the runtime keys are CourseWeekIds. The typed-as-X-keyed-as-Y
   * mismatch is tracked in #2267 (same problem as `covers`).
   */
  sectionLeaderRSVP?: Record<SectionWeekId, boolean>;
  /**
   * Whether the section is a minor section, i.e. the section leader and all
   * members are minors.
   */
  isMinor?: boolean;
  /**
   * The covers of the section for each week.
   *
   * Important: a belated yes RSVP from the primary section leader does not
   * clear the week's entry (`sectionCoverUtils.needsCoverCheck` only stops
   * *new* cover solicitation). The entry is cleared, and moved to
   * {@link releasedCovers}, when the primary starts that week's meeting
   * before the cover has joined (`createZoomMeeting`).
   *
   * TODO: [#2814](https://github.com/CodeinPlace/cip/issues/2814) —
   * Auto-release section covers when SL belatedly RSVPs yes.
   */
  covers?: Record<SectionWeekId, SectionCoverProfile>;
  /**
   * Covers released because the primary section leader started the week's
   * meeting, keyed like `covers`. Written only by `createZoomMeeting`. A
   * released cover who still joins that week keeps their debrief and its
   * recording; they do not regain the cover.
   */
  releasedCovers?: Record<SectionWeekId, {userId: UserId}>;
  /**
   * The section number is a per-time-index unique identifier for the section.
   * Used to avoid collisions when creating sections at the same time.
   */
  sectionNumber?: number;
  /**
   * Whether the section is a test section. Default is false. Useful to
   * temporarily disable sections for testing and hide them from the section
   * monitor/reroutes.
   */
  isTestSection?: boolean;
  /**
   * Whether this is a Super section used as an overflow destination. An
   * omitted value has the same meaning as `false`. Consumers must check
   * `=== true` and must exclude Super sections from traditional capacity and
   * section-switch destination calculations.
   */
  isSuperSection?: boolean;
  /**
   * First meeting date and time for a Super section, stored as an ISO 8601
   * string with its configured UTC offset. Independent of the course's
   * `firstSectionTimestamp` and the traditional-section `timeIndex`.
   */
  superSectionFirstTimestamp?: string;
}

// ============================================================================
// Zod schemas — canonical runtime validators for the shapes above.
// Keep each field in sync with the TypeScript interface; `.loose()` at the
// top level so new fields added by the backend don't cause validation
// failures on read.
// ============================================================================

/**
 * Runtime schema matching {@link SectionCoverProfile}.
 */
export const SectionCoverProfileSchema = z
  .object({
    name: z.string(),
    userId: z.string(),
    courseRole: z.number(),
  })
  .loose();

/**
 * Runtime schema matching {@link SectionDocument}. Every field is optional,
 * mirroring the on-disk sparse shape. `.loose()` preserves unknown keys so
 * docs written by future schema versions still parse.
 */
export const SectionDocumentSchema = z
  .object({
    name: z.string().optional(),
    courseId: z.string().optional(),
    sectionLeader: z.string().optional(),
    bannerImgUrl: z.string().optional(),
    zoomCreatedBy: z.string().optional(),
    zoomJoinLink: z.string().optional(),
    zoomStartLink: z.string().optional(),
    zoomUserEmail: z.string().optional(),
    zoomUserId: z.string().optional(),
    zoomMeetingId: z.string().optional(),
    zoomLink: z.string().optional(),
    zoomMeetingCreatedAt: FirestoreTimestampSchema.optional(),
    timeIndex: z.union([z.number(), z.literal('none')]).optional(),
    weekIndex: z.number().optional(),
    members: z.array(z.string()).optional(),
    guests: z.record(z.string(), z.array(z.string())).optional(),
    teacherAttendance: z.record(z.string(), z.boolean()).optional(),
    zoomPresence: z
      .record(
        z.string(),
        z
          .object({
            startedAt: FirestoreTimestampSchema.optional(),
            hostJoinedAt: FirestoreTimestampSchema.optional(),
            teacherJoinedAt: FirestoreTimestampSchema.optional(),
          })
          .loose(),
      )
      .optional(),
    isActive: z.boolean().optional(),
    sectionLeaderRSVP: z.record(z.string(), z.boolean()).optional(),
    isMinor: z.boolean().optional(),
    covers: z.record(z.string(), SectionCoverProfileSchema).optional(),
    releasedCovers: z
      .record(z.string(), z.object({userId: z.string()}).loose())
      .optional(),
    sectionNumber: z.number().optional(),
    isTestSection: z.boolean().optional(),
    isSuperSection: z.boolean().optional(),
    superSectionFirstTimestamp: z.string().optional(),
  })
  .loose();

/** Firestore path for the sections root collection. */
export const SECTION_COLLECTION_PATH = 'sections';

/**
 * Builds the Firestore path for a single section document.
 *
 * @param sectionId - The section id (or raw string).
 */
export function getSectionDocumentFirebasePath(
  sectionId: SectionId | string,
): string {
  return `${SECTION_COLLECTION_PATH}/${sectionId}`;
}
